import ReceiptLayout from '@/components/common/ReceiptLayout';
import Banner from '@/components/Home/Banner';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { routes } from '@/constant/routes.const';
import TextButton from '@/components/common/TextButton';
import styled from 'styled-components';
import { IGlobalState } from '@/store';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getStoreHome, getStoreReceipt, updateDepartureInfo } from '@/api';
import { VISIT_CUSTOMS_GUIDE } from '@/constant/home.const';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { getDday } from '@repo/utils';
import { DeparturePayload } from '../DepartureDate';
import { CalendarModal, FlexWrap, MTCN } from '@repo/components';
import { MTCN as MTCNType } from '@repo/types';

const Home = () => {
  const { nationality, passportNumber, email, firstRefundDate } =
    useAppSelector((state) => state) as IGlobalState;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [isOpenCalendar, setIsOpenCalendar] = useState(false);
  const [departureDate, setDepartureDate] = useState('');
  const [mtcnList, setMtcnList] = useState<MTCNType[]>([]);

  const { data } = useQuery(
    ['getStoreHome'],
    () => getStoreHome({ passportNumber: passportNumber! }),
    {
      onSuccess: (data) => {
        // 홈클릭 활성화 여부
        dispatch({
          type: 'UPDATE_USER_INFO',
          payload: {
            email: data.email,
            register: data.register,
            paymentStatu: data.paymentStatus,
            departureDate: data.departureDate,
          },
        });
        setDepartureDate(data.departureDate || '');
        setMtcnList(data.mtcnInfo?.mtcnList || []);
      },
    },
  );

  useEffect(() => {
    if (!isOpenCalendar) {
      queryClient.invalidateQueries('getStoreHome');
    }
  }, [isOpenCalendar]);

  const { mutate: departureMutation } = useMutation(
    ['deaprtureDate'],
    (payload: DeparturePayload) => updateDepartureInfo(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('getStoreHome');
      },
      onError: () => {
        alert('서버와 통신이 원활하지 않습니다.');
      },
    },
  );

  // 즉시환급만 있거나 사후환급도 있거나
  // REFUND_ONLY || (BOTH && REFUND_AFTER_ONLY)
  const isImmediateRefundOnly = data?.refundCondition === 'REFUND_ONLY';
  const getVisitCustomsGuide = () => {
    let targetLanguage: 'KOR' | 'CHN' | 'USA' = 'USA';

    if (nationality === 'KOR' || nationality === 'CHN') {
      targetLanguage = nationality;
    }

    return {
      title: VISIT_CUSTOMS_GUIDE[targetLanguage].title,
      contents: VISIT_CUSTOMS_GUIDE[targetLanguage].contents,
    };
  };

  const { data: receiptData = [] } = useQuery(['getStoreReceipt'], () =>
    getStoreReceipt({
      passportNumber,
      latest: true,
      refundAfter: true,
    }),
  );

  const previewReceipt = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'PREVIEW_RECEIPT',
        isActive: true,
        data: { data: { data: receiptData, selectedRefund: 0 } },
      },
    });
  };

  useEffect(() => {
    if (
      localStorage.getItem('isCompleteRefund') &&
      receiptData.length !== 0 &&
      nationality !== 'KOR' &&
      data?.refundCondition !== 'REFUND_ONLY'
    ) {
      previewReceipt();
      localStorage.removeItem('isCompleteRefund');
    }
  }, [receiptData, isImmediateRefundOnly]);

  useEffect(() => {
    if (
      data &&
      data?.refundCondition !== 'REFUND_ONLY' &&
      !data?.departureDate
    ) {
      setIsOpenCalendar(true);
    }
  }, [data]);

  return (
    <ReceiptLayout>
      <Wrapper>
        <ButtonWrapper>
          {data?.refundCondition !== 'REFUND_ONLY' && (
            <DepartureButton
              type='button'
              onClick={() => setIsOpenCalendar(true)}
            >
              {data?.departureDate ? (
                <>
                  {nationality === 'KOR' ? '출국 예정일까지 ' : 'Departure '}
                  {getDday(data?.departureDate)}
                </>
              ) : (
                <>
                  {nationality === 'KOR'
                    ? '출국 예정일 선택'
                    : 'Select Departure Date'}
                </>
              )}
            </DepartureButton>
          )}
          <div />
        </ButtonWrapper>
        {data && (
          <Banner
            refund={data?.refundAmount}
            refundAfter={data?.refundAfterAmount}
            isImmediateRefund={isImmediateRefundOnly}
            paymentStatus={data?.paymentStatus || ''}
          />
        )}
        {mtcnList.length > 0 && (
          <SlideWrapper>
            <MTCN data={mtcnList} />
          </SlideWrapper>
        )}
        {!isImmediateRefundOnly && (
          <GuideWrapper>
            <GuideTitle>{getVisitCustomsGuide().title}</GuideTitle>
            <GuideContents>{getVisitCustomsGuide().contents}</GuideContents>
          </GuideWrapper>
        )}
        <TextButton
          title={
            nationality === 'KOR'
              ? '정보 수정'
              : nationality === 'CHN'
                ? '修改汇款信息'
                : 'Edit Transfer Information'
          }
          onClick={() => {
            history.push(routes.refundInfo);
          }}
          color='#1856CD'
          bgColor='#fff'
          outline
        />
      </Wrapper>
      {isOpenCalendar && (
        <CalendarModal
          onChange={setDepartureDate}
          departureDate={departureDate}
          firstRefundDate={firstRefundDate}
          onClose={() => setIsOpenCalendar(false)}
          onClick={() => {
            departureMutation({
              passportNumber,
              departureDate: format(new Date(departureDate), 'yyyy-MM-dd'),
              email,
            });
            setIsOpenCalendar(false);
          }}
        />
      )}
    </ReceiptLayout>
  );
};

export default Home;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 28px;
`;

const GuideWrapper = styled.div`
  padding: 16px;
  border-radius: 10px;
  background: #f5f6f7;
`;

const GuideTitle = styled.p`
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: #3a3b3e;
`;

const GuideContents = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  color: #80848a;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DepartureButton = styled.button`
  padding: 4px 16px;
  color: #fff;
  border-radius: 100px;
  background: #246cf6;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const SlideWrapper = styled(FlexWrap)`
  flex-direction: column;
  justify-content: center;
  width: 335px;
  margin: auto;
`;
